import type { SynthPatch } from './synthVoice';

// Audio asset registry — DATA, loaded from public/audio/manifest.json at boot
// (contract change 2026-06-11; was a hand-edited TS array). Any lane can now
// register a sound as a JSON edit, every asset carries a `license` from day
// one, and validate.mjs reads the same file the runtime does.
//
// Two asset kinds (A13):
//   sample — a `url` fetched + decoded at load (mp3/m4a/ogg/wav/opus)
//   synth  — a `patch` (osc/noise/filter/env/lfo data, see synthVoice.ts);
//            zero bytes on the wire, built live by AudioManager.play() into
//            the SAME ownGain → [panner] → channel chain as samples.
//
// Naming convention:
//  - `test_music_*` — placeholder tracks until real licensing lands
//  - `sfx_*`   — recorded effects/beds (compress-audio from Working-gitignore)
//  - `synth_*` — procedural patches (license: cleared by construction)
export type AudioAssetChannel = 'ambient' | 'music' | 'narration' | 'sfx';

/** Licensing status — tracked per asset from day one so the swap to cleared
 *  material is data, not code. 'project' = produced for this project (e.g.
 *  narration pulled from the directors' film); 'original' = John's OWN
 *  recordings authored for this project (the narration MVP — the first
 *  non-placeholder, fully-owned assets, distinct from third-party project
 *  footage). */
export type AudioLicense = 'cleared' | 'original' | 'pending' | 'placeholder' | 'project' | 'unknown';

export interface AudioAsset {
  id: string;
  kind?: 'sample' | 'synth';
  /** sample kind — source file. */
  url?: string;
  /** synth kind — the voice definition (data; see SynthPatch). */
  patch?: SynthPatch;
  /** Suggested channel — purely advisory; PlayOptions.channel wins at call site. */
  defaultChannel?: AudioAssetChannel;
  /** Hint that this asset is meant to loop seamlessly. The audio manager
   *  doesn't act on this directly — `audio.play({ loop: true })` is still
   *  required at call site — but the dev panel uses it to mark loopable beds. */
  loopable?: boolean;
  license?: AudioLicense;
}

/** Load + minimally validate the registry. Entries missing the basics are
 *  warned and skipped (the S2 philosophy: never die on a hand-edited file).
 *  Returns [] only if the file itself is missing/unparseable — the scene then
 *  simply has no audio palette, which is loud enough to notice. */
export async function loadAudioManifest(url = '/audio/manifest.json'): Promise<AudioAsset[]> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json() as { assets?: unknown[] };
    const out: AudioAsset[] = [];
    const seen = new Set<string>();
    for (const raw of data.assets ?? []) {
      const a = raw as AudioAsset;
      if (typeof a.id !== 'string' || !a.id) { console.warn('[audio manifest] entry without id — skipped', raw); continue; }
      if (seen.has(a.id)) { console.warn(`[audio manifest] duplicate id "${a.id}" — keeping the first`); continue; }
      const kind = a.kind === 'synth' ? 'synth' : 'sample';
      if (kind === 'sample' && typeof a.url !== 'string') { console.warn(`[audio manifest] sample "${a.id}" has no url — skipped`); continue; }
      if (kind === 'synth' && (!a.patch || !Array.isArray(a.patch.voices))) { console.warn(`[audio manifest] synth "${a.id}" has no patch.voices — skipped`); continue; }
      seen.add(a.id);
      out.push({ ...a, kind });
    }
    return out;
  } catch (e) {
    console.warn('[audio manifest] load failed — no audio palette this session', e);
    return [];
  }
}
